// Thẻ của hộp hướng dẫn trang Thanh toán — MỘT nguồn, buộc vào nhãn thật của màn.
//
// Bài hướng dẫn nói về chín thẻ mà người đọc đang nhìn thấy, nên tên thẻ trong
// bài phải là CHÍNH tên trên màn, không phải một bản chép tay trôi dần. Vì thế
// nhãn lấy thẳng từ `TAB_LABEL` của màn, và danh sách khoá dưới đây khai
// `satisfies` theo đúng khoá của từ điển đó: ai đổi khoá thẻ trên màn sẽ gặp
// lỗi kiểu NGAY LÚC BUILD, không phải đợi ai đó mở hộp lên mới thấy bài chỉ
// tới một thẻ không còn tồn tại.
//
// Cách bày hộp nằm ở `MultiTabGuideDialog`, dùng chung với ba trang ca; file
// này chỉ khai phần riêng của trang Thanh toán.

// Chỉ mượn KIỂU của từ điển nhãn: file này khai thứ tự thẻ, còn chữ hiển thị
// do bài văn lấy thẳng từ `TAB_LABEL`. Nhập kiểu thì bó buộc vẫn còn nguyên mà
// không kéo giá trị vào gói.
import type { TAB_LABEL } from "@/features/payments/labels";
import type { Audience } from "./audience";
import type { StatusTone } from "./GuideSections";
import type { GuideFlow, MultiTabGuideConfig } from "./multi-tab-guides";

/** Id trang dùng hộp riêng này. Tra bằng hằng số, không gõ chuỗi lần hai. */
export const PAYMENT_GUIDE_ID = "payment";

/**
 * Thứ tự thẻ trong hộp = thứ tự thẻ trên màn.
 *
 * Người đọc dò bài theo hàng thẻ họ đang nhìn; xếp khác đi là bắt họ đối chiếu
 * hai thứ tự trong đầu.
 */
export const PAYMENT_GUIDE_TAB_ORDER = [
  "due",
  "waiting",
  "collected",
  "queue",
  "approve",
  "review",
  "bank",
  "accounting",
  "tips",
  "config",
] as const satisfies readonly (keyof typeof TAB_LABEL)[];

export type PaymentGuideTabKey = (typeof PAYMENT_GUIDE_TAB_ORDER)[number];

/**
 * Nhóm nghề nào MỞ ĐƯỢC thẻ nào trên màn thật.
 *
 * Bảng này là dữ liệu kỹ thuật để hộp bày đúng số thẻ, KHÔNG phải nội dung
 * hiển thị — không câu chữ nào trong bài được nói lại điều này, vì "ai mở được
 * gì" chính là thứ ranh giới thông tin cấm kể.
 *
 * Vì sao phải khai tay thay vì suy từ "thẻ nào có bài của họ": hai thứ đó lệch
 * nhau và lệch theo hướng nguy hiểm. Người không có bài ở thẻ nào cả (hậu kỳ
 * chẳng hạn) mà rơi vào nhánh "thôi hiện hết" là đọc trọn bộ chín thẻ — đo
 * 12/09/2026 trên bàn xem thử, đúng cái rò mà cả tính năng này sinh ra để chặn.
 * Ngược lại, người mở được một thẻ nhưng bài chưa viết khối cho họ thì vẫn nên
 * thấy thẻ đó để đọc phần chung.
 *
 * Nguồn: `features/payments/permissions.ts` + bảng hành động của máy chủ. Đổi
 * rào bên đó thì sửa cả bảng này.
 */
export const THE_AI_MO_DUOC: Record<PaymentGuideTabKey, Audience[]> = {
  // Thu tiền: quầy, kinh doanh, kế toán, và người điều hành cơ sở.
  due: ["reception", "sale", "accounting", "lead", "exec"],
  // Hai thẻ này không rào — ai vào được trang là thấy.
  waiting: ["reception", "sale", "photo", "makeup", "retouch", "cskh", "marketing", "accounting", "qc", "lead", "exec"],
  collected: ["reception", "sale", "photo", "makeup", "retouch", "cskh", "marketing", "accounting", "qc", "lead", "exec"],
  // Ba hàng đợi đối chiếu: nghề kế toán, cùng vai quản trị kỹ thuật.
  queue: ["accounting", "exec"],
  // Hàng phiếu chờ duyệt mở thêm người điều hành cơ sở để XEM (QĐ 0447) —
  // nhóm "lead" rộng hơn đúng vai được mở trên màn, cùng kiểu xấp xỉ với `due`.
  approve: ["accounting", "lead", "exec"],
  review: ["accounting", "exec"],
  // Sổ ngân hàng cố ý HẸP HƠN ba thẻ trên: chỉ nghề kế toán (quyết định 0067).
  bank: ["accounting"],
  // Việc kế toán sửa tay bên Kế toán (QĐ 0311): kế toán + giám đốc, quản trị.
  accounting: ["accounting", "exec"],
  tips: ["accounting", "exec"],
  config: ["exec"],
};

/**
 * Màu nhãn trạng thái, khớp badge trên màn thật.
 *
 * Tra theo NHÃN chứ không theo thẻ: cùng một nhãn xuất hiện ở nhiều thẻ và
 * phải mang cùng một màu ở mọi nơi — đó chính là thứ giúp người đọc nhận ra nó.
 */
const TONE_THEO_NHAN: Record<string, StatusTone> = {
  "Đã thu": "success",
  Đủ: "success",
  "Đã khớp": "success",
  "Đã xác nhận": "success",
  "Đang chờ khách chuyển": "info",
  "Chờ kế toán duyệt": "warning",
  "Chờ xác nhận": "warning",
  "Chờ thanh toán": "warning",
  "Chưa khớp": "warning",
  "⚠️ Sai nội dung CK": "danger",
  "Đã từ chối": "danger",
};

/**
 * Vòng đời một phiếu thu — bốn chặng, thẻ nào của màn nhìn thấy chặng nào.
 *
 * Vì sao hộp cần cái này: chín thẻ của màn là chín LÁT CẮT của cùng một dòng
 * tiền, nhưng đứng trong một thẻ thì không thấy được điều đó — người đọc học
 * thuộc "thẻ này làm gì" mà vẫn không hiểu vì sao một phiếu biến khỏi thẻ mình
 * rồi hiện ra ở thẻ người khác.
 */
const PAYMENT_FLOW: GuideFlow = {
  heading: "Một phiếu thu đi qua đâu",
  intro:
    "Chín thẻ của trang là chín lát cắt của cùng một mạch. Chặng tô đậm là chỗ thẻ bạn đang mở nhìn vào.",
  stages: [
    {
      ten: "Lập phiếu",
      tone: "info",
      ke: "Khách trả tiền, có người mở một phiếu thu cho đơn — phát mã, ghi tiền mặt, hoặc ghi tay khoản khách báo đã chuyển.",
      the: ["due"],
    },
    {
      ten: "Chờ",
      tone: "warning",
      ke: "Phiếu đứng lại: hoặc chờ khách chuyển, hoặc tiền đã về mà chờ người xác nhận.",
      the: ["waiting", "approve", "queue"],
    },
    {
      ten: "Đã thu",
      tone: "success",
      ke: "Tiền đã về và đã ghi đúng đơn. Phiếu vào sổ, đơn bớt nợ.",
      the: ["collected", "bank"],
    },
    {
      ten: "Còn phải dò lại",
      tone: "danger",
      nhanh: true,
      ke: "Nhánh rẽ của chặng trên: phiếu đã thu nhưng lúc xác nhận có tích dấu khách ghi sai nội dung, nên còn phải đối chiếu sao kê.",
      the: ["review"],
    },
  ],
};

export const PAYMENT_GUIDE: MultiTabGuideConfig = {
  tabOrder: PAYMENT_GUIDE_TAB_ORDER,
  theAiMoDuoc: THE_AI_MO_DUOC,
  tone: TONE_THEO_NHAN,
  flow: PAYMENT_FLOW,
};
